/**
 * NOLAS 2026, © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/utils/formatEventPlace.ts
 *
 * Rôle :
 *   LOT 3 (socle d'identité) : Formatage FR du LIEU de l'événement, DÉRIVÉ de
 *   la source autoritaire `tne_events` (via useEventConfig : `venue_name` /
 *   `venue_address`). Comme `formatEventDate.ts`, il alimente les tokens
 *   d'interpolation `{eventPlace}` / `{eventPlaceAddress}` des clés i18n
 *   migrées, on ne fige plus « SPOT de Thionville » en dur dans le catalogue.
 *
 * Deux formes :
 *   - `'short'` → le NOM du lieu seul (« SPOT de Thionville »).
 *   - `'long'`  → nom + rue (« SPOT de Thionville : Place de la Liberté »).
 *
 * Pourquoi séparer la « rue » de la « ville » :
 *   `venue_address` est saisie « rue, ville » (ex. « Place de la Liberté,
 *   Thionville »). La forme longue veut la RUE sans répéter la ville déjà
 *   contenue dans le nom du lieu. On coupe donc à la DERNIÈRE virgule (la ville
 *   est en fin), heuristique simple, documentée, suffisante pour une adresse
 *   « rue, ville ». {@link formatEventPlaceStreet} expose cette rue seule pour
 *   les clés qui composent leur propre séparateur (virgule vs tiret cadratin).
 *
 * Réutilisable : 100 %, toute app DRANE / tout salon affichant un lieu FR.
 *
 * Voir : hooks/useEventConfig.ts (source du lieu), utils/formatEventTime.ts
 *        (modèle), context/EventIdentityProvider.tsx (consommateur).
 *
 * @author Mikael
 * @since  2026-07-23 (LOT 3, socle d'identité)
 */

/** Formes de rendu du lieu : nom seul (`short`) ou nom + rue (`long`). */
export type EventPlaceFormat = 'short' | 'long';

/** Normalise une chaîne optionnelle : trim + `null` si vide. */
function nettoyer(valeur: string | null | undefined): string | null {
  if (typeof valeur !== 'string') return null;
  const t = valeur.trim();
  return t.length > 0 ? t : null;
}

/**
 * Extrait la RUE d'une adresse « rue, ville » (partie AVANT la dernière
 * virgule). Sans virgule, renvoie l'adresse entière. Renvoie `null` si vide.
 *
 * @example formatEventPlaceStreet({ venue_address: 'Place de la Liberté, Thionville' }) // 'Place de la Liberté'
 */
export function formatEventPlaceStreet(
  config: { readonly venue_address?: string | null },
): string | null {
  const adresse = nettoyer(config.venue_address);
  if (!adresse) return null;
  const i = adresse.lastIndexOf(',');
  // i > 0 : il y a bien une partie « rue » non vide avant la virgule.
  return i > 0 ? adresse.slice(0, i).trim() : adresse;
}

/**
 * Construit le lieu FR de l'événement à partir de son nom et de son adresse.
 * Tolérant (offline-first §18) : si le nom manque, retombe sur la rue ; si tout
 * manque, renvoie `null` (le consommateur masque alors le lieu).
 *
 * @param config Nom (`venue_name`) et adresse (`venue_address`) du lieu.
 * @param format `'short'` (nom seul) ou `'long'` (nom, rue). Défaut `'short'`.
 * @returns Le lieu FR, ou `null` si indéterminable.
 *
 * @example formatEventPlace({ venue_name: 'SPOT de Thionville' })                                   // 'SPOT de Thionville'
 * @example formatEventPlace({ venue_name: 'SPOT de Thionville', venue_address: 'Place de la Liberté, Thionville' }, 'long') // 'SPOT de Thionville : Place de la Liberté'
 */
export function formatEventPlace(
  config: { readonly venue_name?: string | null; readonly venue_address?: string | null },
  format: EventPlaceFormat = 'short',
): string | null {
  const nom = nettoyer(config.venue_name);
  if (format === 'short') return nom;
  const rue = formatEventPlaceStreet(config);
  // 2026-10-04 (règle de Mika, aucun tiret cadratin) : une virgule sépare
  // le nom du lieu et la rue. Mikael - chef de projet - 2026-10-04
  if (nom && rue) return `${nom}, ${rue}`;
  return nom ?? rue ?? null;
}
